import React, {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ButtonExit from "../../../../components/UI/button/ButtonExit";
import ButtonAdd from "../../../../components/UI/button/ButtonAdd";
import {DEFAULT_HANDLE_SIZE_MM} from "./tds/parse3dsToHandle";

/**
 * Импорт / редактирование ручки из 3DS.
 * Стиль как у NameModal / CurrencyModal.
 */
const HandleImportDialog = ({
    open,
    onClose,
    onConfirm,
    fileName = "",
    meta = null,
    parseError = null,
    busy = false,
    mode = "import",
    initialValues = null,
}) => {
    const isEdit = mode === "edit";
    const [position, setPosition] = useState("");
    const [name, setName] = useState("");
    const [sizeMm, setSizeMm] = useState(String(DEFAULT_HANDLE_SIZE_MM));
    const [sizeError, setSizeError] = useState("");

    useEffect(() => {
        if (!open) return;
        setSizeError("");
        if (isEdit && initialValues) {
            setPosition(String(initialValues.position ?? ""));
            setName(String(initialValues.name ?? "") || "Ручка");
            setSizeMm(String(initialValues.sizeMm ?? DEFAULT_HANDLE_SIZE_MM));
            return;
        }
        const base = String(fileName || "")
            .replace(/\.[^.]+$/, "")
            .trim();
        setPosition("");
        setName(base || "Ручка");
        setSizeMm(String(meta?.defaultSizeMm ?? DEFAULT_HANDLE_SIZE_MM));
    }, [open, fileName, isEdit, initialValues, meta]);

    const handleClose = () => {
        if (busy) return;
        onClose?.();
    };

    const handleConfirm = () => {
        const size = Number(String(sizeMm).replace(",", "."));
        if (!Number.isFinite(size) || size < 1) {
            setSizeError("Размер должен быть ≥ 1 мм");
            return;
        }
        onConfirm?.({
            name: String(name || "").trim() || "Ручка",
            position: String(position || "").trim(),
            sizeMm: size,
        });
    };

    const canConfirm = isEdit
        ? !busy
        : Boolean(meta) && !parseError && !busy;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                style: {
                    minWidth: 400,
                    borderRadius: 8,
                },
            }}
        >
            <DialogTitle textAlign="center">
                {isEdit ? "Редактировать ручку" : "Импорт ручки (3DS)"}
            </DialogTitle>
            <DialogContent sx={{padding: "13px 24px", minWidth: 350}}>
                {!isEdit && parseError ? (
                    <Typography color="error" textAlign="center" sx={{mt: 1}}>
                        {parseError}
                    </Typography>
                ) : (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, mt: 0.5}}>
                        {meta && (
                            <Typography variant="body2" color="text.secondary">
                                Мешей: {meta.meshCount}
                                {meta.maxDimMeters != null && (
                                    <>
                                        {" · "}
                                        габарит модели ≈ {(meta.maxDimMeters * 1000).toFixed(0)} мм (после перевода единиц)
                                    </>
                                )}
                            </Typography>
                        )}
                        <TextField
                            label="Позиция"
                            size="small"
                            fullWidth
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            disabled={busy}
                            autoFocus
                        />
                        <TextField
                            label="Наименование"
                            size="small"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={busy}
                        />
                        <TextField
                            label="Размер по длинной стороне, мм"
                            size="small"
                            fullWidth
                            value={sizeMm}
                            onChange={(e) => {
                                setSizeMm(e.target.value);
                                setSizeError("");
                            }}
                            error={Boolean(sizeError)}
                            helperText={sizeError || "3DS без единиц — задайте желаемый размер ручки"}
                            disabled={busy}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{px: "1.25rem", py: 1.5, gap: 1}}>
                <ButtonExit
                    text="Отменить"
                    onClick={handleClose}
                    width={96}
                    sx={{
                        height: 40,
                        fontSize: "14px",
                        ...(busy ? {pointerEvents: "none", opacity: 0.5} : null),
                    }}
                />
                {(isEdit || !parseError) && (
                    <ButtonAdd
                        text={isEdit ? "Сохранить" : "Добавить"}
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        loading={busy}
                        width={96}
                        height={40}
                        fontSize="14px"
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default HandleImportDialog;
